/*
 * Account avatars: an uploaded picture (resized here, stored with the account as a small data URL), else the Google
 * picture, else initials on an accent colour the person picks. The colour ids are validated by the backend
 * (server AVATAR_COLORS) — keep the two lists in step.
 */

export const AVATAR_COLORS = [
  { id: 'violet', label: 'Violet', bg: 'hsl(262 83% 58%)' },
  { id: 'cyan', label: 'Cyan', bg: 'hsl(189 94% 38%)' },
  { id: 'amber', label: 'Amber', bg: 'hsl(38 92% 44%)' },
  { id: 'green', label: 'Green', bg: 'hsl(142 71% 38%)' },
  { id: 'rose', label: 'Rose', bg: 'hsl(350 80% 55%)' },
  { id: 'blue', label: 'Blue', bg: 'hsl(217 91% 55%)' },
  { id: 'slate', label: 'Slate', bg: 'hsl(215 16% 42%)' },
  { id: 'orange', label: 'Orange', bg: 'hsl(25 95% 48%)' },
];

export const avatarColorCss = (id) => (AVATAR_COLORS.find((c) => c.id === id) || {}).bg || null;

/** Two letters for the initials fallback (first + last word, or the first two characters). */
export const initialsOf = (user) => {
  const src = (user?.name || user?.email || '?').trim();
  const parts = src.split(/\s+/).filter(Boolean);
  const two = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : src.slice(0, 2);
  return two.toUpperCase();
};

/** The picture to show, if any: the upload wins over the Google picture. */
export const avatarSrc = (user) => user?.avatar || user?.picture || null;

/** Longest side of the stored avatar (px). 160 px JPEG ≈ 6–12 KB — the backend caps uploads at 200 KB. */
export const AVATAR_SIZE = 160;

/**
 * Read an image file, centre-crop it to a square and shrink it to AVATAR_SIZE. Resolves with a JPEG data URL
 * (PNG when the picture has transparency is not needed for a round avatar — JPEG keeps it small).
 */
export const fileToAvatarDataUrl = (file) => new Promise((resolve, reject) => {
  if (!file || !/^image\/(png|jpe?g|webp|gif|bmp)$/i.test(file.type)) { reject(new Error('Choose a PNG, JPEG or WebP picture.')); return; }
  if (file.size > 12 * 1024 * 1024) { reject(new Error('That picture is very large — try one under 12 MB.')); return; }
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    try {
      const side = Math.min(img.naturalWidth, img.naturalHeight);
      const sx = (img.naturalWidth - side) / 2;
      const sy = (img.naturalHeight - side) / 2;
      const canvas = document.createElement('canvas');
      canvas.width = AVATAR_SIZE;
      canvas.height = AVATAR_SIZE;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, AVATAR_SIZE, AVATAR_SIZE);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, sx, sy, side, side, 0, 0, AVATAR_SIZE, AVATAR_SIZE);
      resolve(canvas.toDataURL('image/jpeg', 0.86));
    } catch (e) {
      reject(new Error('That picture could not be read.'));
    } finally {
      URL.revokeObjectURL(url);
    }
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That picture could not be read.')); };
  img.src = url;
});

/** Folder dot colour — folders share the account accent palette; grey when none is set. */
export const folderColorCss = (id) => avatarColorCss(id) || 'hsl(var(--muted-foreground))';
